import { FocusMonitor } from '@angular/cdk/a11y'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ChangeDetectionStrategy,
} from '@angular/core'
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms'
import { Subject } from 'rxjs'

import { MatFormFieldControl } from '@angular/material/form-field'
import { defaultTinyMCEOptions } from '../../components'

@Component({
  selector: 'app-mat-html-input',
  templateUrl: './mat-html-input.component.html',
  styleUrls: ['mat-html-input.component.scss'],
  providers: [{ provide: MatFormFieldControl, useExisting: MatHTMLInputComponent }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MatHTMLInputComponent implements ControlValueAccessor, MatFormFieldControl<string>, OnDestroy, DoCheck {
  static nextId = 0

  controlType = 'mat-html-input'

  editorOptions = {
    ...defaultTinyMCEOptions,
    min_height: 100,
    max_height: 200,
    toolbar: false,
  }

  @HostBinding() id = `${this.controlType}-${MatHTMLInputComponent.nextId++}`
  @HostBinding('attr.aria-describedby') describedBy = ''

  stateChanges = new Subject<void>()
  errorState = false

  focused = false
  touched = false

  _placeholder: string
  _required = false
  _disabled = false

  ctrl = new UntypedFormControl()

  @Output() valueChange = new EventEmitter<string>()

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    public elementRef: ElementRef,
    public focusMonitor: FocusMonitor
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this
    }

    focusMonitor.monitor(elementRef.nativeElement, true).subscribe((origin) => {
      setTimeout(() => {
        let focused = !!origin

        this.focused = focused
        this.stateChanges.next()
      })
    })

    this.ctrl.valueChanges.subscribe((value) => {
      if (this.onChange) this.onChange(value)
      this.stateChanges.next()
    })
  }

  ngOnDestroy() {
    this.stateChanges.complete()
    this.focusMonitor.stopMonitoring(this.elementRef.nativeElement)
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched
      this.stateChanges.next()
    }
  }

  onChange = (value: string) => {}

  onTouched = () => {
    this.touched = true
    this.ctrl.markAsTouched()
  }

  writeValue(value: string): void {
    this.ctrl.setValue(value)
  }

  registerOnChange(fn): void {
    this.onChange = fn
  }

  registerOnTouched(fn): void {
    this.onTouched = fn
  }

  @Input()
  get value() {
    return this.ctrl.value
  }

  set value(value: string) {
    this.ctrl.setValue(value, { emitEvent: false })

    if (this.onChange) this.onChange(value)
    this.stateChanges.next()
  }

  @Input()
  get placeholder() {
    return this._placeholder
  }

  set placeholder(placeholder: string) {
    this._placeholder = placeholder
    this.stateChanges.next()
  }

  @Input()
  get required() {
    return this._required
  }

  set required(required) {
    this._required = coerceBooleanProperty(required)
    this.stateChanges.next()
  }

  @Input()
  get disabled() {
    return this._disabled
  }

  set disabled(dis) {
    this._disabled = coerceBooleanProperty(dis)
    this.stateChanges.next()
    if (this._disabled) {
      this.ctrl.disable({ emitEvent: false })
    } else {
      this.ctrl.enable({ emitEvent: false })
    }
  }

  get empty() {
    return !this.ctrl.value
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ')
  }

  onContainerClick() {}
}
